// Template validation for the editor: the GS1 Logistic Label rules the research lists as
// checkable, elements outside the page, text that will not fit, unknown fields and filters.
// Warnings, never blockers — the office decides (NiceLabel blocks printing by default and
// users find that surprising).
import { knownPaths, SAMPLES } from "./data.js";
import { parseElementString } from "./gs1.js";
import { layout } from "./layout.js";
import { textWidthMm } from "./metrics.js";
import { isLabelKind } from "./model.js";
import { checkText, renderText } from "./text.js";
export function checkTemplate(t) {
    const out = [];
    const known = knownPaths(t.kind);
    const sample = SAMPLES[t.kind];
    const { width, height } = t.page;
    const ids = new Set();
    const ssccBarcodes = [];
    let lowestBarcode = -1;
    for (const e of t.elements) {
        if (ids.has(e.id))
            out.push({ elementId: e.id, message: "Två element har samma id" });
        ids.add(e.id);
        const bottom = e.band === "page" && e.anchor === "bottom" ? height - e.y : e.y + e.h;
        if (e.x < -0.01 || e.y < -0.01 || e.x + e.w > width + 0.01 || (e.anchor === "top" && e.y + e.h > height + 0.01))
            out.push({ elementId: e.id, message: "Elementet sticker utanför sidan" });
        if (e.visibleWhen.trim())
            for (const p of checkText(`{{ ${e.visibleWhen} }}`, known))
                out.push({ elementId: e.id, message: `Villkor: ${p}` });
        if (e.type === "text") {
            for (const p of checkText(e.text, known))
                out.push({ elementId: e.id, message: p });
            if (e.fit === "none" && !e.wrap) {
                const s = renderText(e.text, sample);
                const turned = e.rotation === 90 || e.rotation === 270;
                if (s && textWidthMm(s, e.font) > (turned ? e.h : e.w) + 0.01)
                    out.push({ elementId: e.id, message: `Texten får inte plats med exempeldata ("${s.slice(0, 24)}…") — välj Krymp eller Radbryt` });
            }
        }
        else if (e.type === "barcode") {
            for (const p of checkText(e.value, known))
                out.push({ elementId: e.id, message: p });
            const turned = e.rotation === 90 || e.rotation === 270;
            const barsH = (turned ? e.w : e.h) - (e.hri === "none" ? 0 : e.hriFont.size * 1.3 / (72 / 25.4));
            if (e.symbology.startsWith("gs1")) {
                const v = renderText(e.value, sample);
                const segs = parseElementString(v);
                if (!segs)
                    out.push({ elementId: e.id, message: "GS1-värdet måste skrivas med AI i parentes, t.ex. (00){{ sscc }}" });
                else {
                    const seen = new Set();
                    let sawVariable = false;
                    for (const s of segs) {
                        if (seen.has(s.ai))
                            out.push({ elementId: e.id, message: `AI (${s.ai}) förekommer två gånger` });
                        seen.add(s.ai);
                        if (s.fixed && sawVariable)
                            out.push({ elementId: e.id, message: `AI (${s.ai}) har fast längd och bör stå före de variabla` });
                        if (!s.fixed)
                            sawVariable = true;
                    }
                    if (seen.has("00")) {
                        ssccBarcodes.push({ id: e.id, bottom });
                        if (segs.length > 1 && isLabelKind(t.kind) && Math.max(width, height) <= 152)
                            out.push({ elementId: e.id, message: "På en etikett upp till A6 ska SSCC stå ensam i sin streckkod (GS1)" });
                    }
                    if (e.symbology === "gs1-128") {
                        if (barsH < 31.75 && isLabelKind(t.kind) && seen.has("00"))
                            out.push({ elementId: e.id, message: `GS1-128 på logistiketikett: strecken bör vara minst 32 mm höga (nu ${barsH.toFixed(0)})` });
                        if (e.moduleWidth > 0 && (e.moduleWidth < 0.495 || e.moduleWidth > 1.016))
                            out.push({ elementId: e.id, message: "GS1-128: X-dimension 0,495–1,016 mm" });
                        if (e.quietZone < (e.moduleWidth || 0.5) * 10)
                            out.push({ elementId: e.id, message: `Tyst zon bör vara minst ${((e.moduleWidth || 0.5) * 10).toFixed(1)} mm (10 × X)` });
                        if (e.hri !== "none" && e.hriFont.size < 8.5)
                            out.push({ elementId: e.id, message: "Klartext under GS1-128 bör vara minst 3 mm hög (≈ 8,5 pt)" });
                    }
                }
            }
            if (!e.symbology.startsWith("gs1"))
                lowestBarcode = Math.max(lowestBarcode, bottom);
        }
        else if (e.type === "table") {
            if (!e.rows.trim())
                out.push({ elementId: e.id, message: "Tabellen saknar radkälla" });
            else
                for (const p of checkText(`{{ ${e.rows} }}`, known))
                    out.push({ elementId: e.id, message: p });
            if (e.columns.length === 0)
                out.push({ elementId: e.id, message: "Tabellen har inga kolumner" });
            for (const c of e.columns)
                for (const p of checkText(c.header, known).concat(checkText(c.value, known)))
                    out.push({ elementId: e.id, message: `Kolumn ${c.header || c.id}: ${p}` });
        }
    }
    // SSCC must be in the lowest barcode on a label.
    if (isLabelKind(t.kind) && ssccBarcodes.length > 0) {
        const sscc = Math.max(...ssccBarcodes.map((b) => b.bottom));
        if (lowestBarcode > sscc + 0.01)
            out.push({ elementId: ssccBarcodes[0].id, message: "SSCC ska vara den lägst placerade streckkoden på etiketten (GS1)" });
    }
    // Whatever the layout engine finds with the sample data.
    try {
        for (const w of layout(t, sample).warnings)
            if (!out.some((o) => o.elementId === w.elementId && o.message === w.message))
                out.push(w);
    }
    catch (e) {
        out.push({ elementId: null, message: e instanceof Error ? e.message : String(e) });
    }
    return out;
}
//# sourceMappingURL=check.js.map